/* eslint-disable  */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { bindActionCreators } from "redux";
import { Search, RefreshCw, Plus, Sliders, Printer, Edit3 } from "lucide-react";
import { inventoryActionCreators } from "../../services/Actions";
import axiosInstance from "../../services/Axios/axios";
import RenderInventoryPage from "../RenderInventoryPage";
import Loader from "../../Components/Loader";
import ProductModal from "../../HirePurchaseModel/Modal/AddProductModal";
import TablePagination from "../components/TablePagination";

const PAGE_SIZE = 10;

function InventoryItems() {
  const dispatch = useDispatch();
  const {
    GetInventoryItems,
    CreateInventoryItem,
    UpdateInventoryItem,
    AdjustStock,
    ImportFromProducts,
    PrintLabel,
    ResetInventoryResponse,
  } = bindActionCreators(inventoryActionCreators, dispatch);

  const inventory = useSelector((state) => state.inventory);
  const { items, response, isLoading } = inventory;

  const [search, setSearch] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdjust, setOpenAdjust] = useState(false);

  const [addForm, setAddForm] = useState({
    product: "",
    stock_on_hand: 0,
    reorder_level: 0,
    reorder_qty: 0,
    bin_location: "",
    cost_price: 0,
  });
  const [editForm, setEditForm] = useState({
    reorder_level: 0,
    reorder_qty: 0,
    bin_location: "",
    cost_price: 0,
    status: "active",
  });
  const [adjustForm, setAdjustForm] = useState({ qty_delta: 0, reason: "" });
  const [page, setPage] = useState(1);

  const fetchItems = () =>
    GetInventoryItems({
      search,
      low_stock: lowOnly ? "true" : undefined,
    });

  useEffect(() => {
    fetchItems();
  }, [lowOnly]);

  // Reset to first page whenever the item list changes.
  useEffect(() => {
    setPage(1);
  }, [items]);

  const pagedItems = (items || []).slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Load products (for the Add-item picker). Handles paginated or plain array.
  const fetchProducts = async () => {
    try {
      const res = await axiosInstance.get(`/products?isPaginated=false`);
      setProducts(res.data?.docs || res.data || []);
    } catch (_) { }
  };

  useEffect(() => {
    if (openAdd && products.length === 0) fetchProducts();
  }, [openAdd]);

  useEffect(() => {
    if (selected) {
      setEditForm({
        reorder_level: selected?.reorder_level ?? 0,
        reorder_qty: selected?.reorder_qty ?? 0,
        bin_location: selected?.bin_location ?? "",
        cost_price: selected?.cost_price ?? 0,
        status: selected?.status ?? "active",
      });
    }
  }, [selected]);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setOpenAdd(false);
      setOpenEdit(false);
      setOpenAdjust(false);
      setAddForm({ product: "", stock_on_hand: 0, reorder_level: 0, reorder_qty: 0, bin_location: "", cost_price: 0 });
      setAdjustForm({ qty_delta: 0, reason: "" });
      setTimeout(() => {
        ResetInventoryResponse();
        fetchItems();
      }, 1200);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetInventoryResponse(), 1500);
    }
  }, [response]);

  const Badge = ({ value, threshold }) => {
    const low = Number(value) <= Number(threshold ?? 0);
    return (
      <span
        className={`px-2.5 py-1 text-xs font-bold rounded-full ${low ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
      >
        {value}
      </span>
    );
  };

  return (
    <RenderInventoryPage title={"Stock Items"}>
      {isLoading && <Loader />}

      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchItems()}
              placeholder="Search SKU / barcode / product"
              className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg w-72 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <button onClick={fetchItems} className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Search</button>
          <label className="flex items-center gap-2 text-sm text-gray-600 ml-2">
            <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} className="rounded" />
            Low stock only
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => ImportFromProducts()}
            className="flex items-center gap-2 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 font-medium rounded-lg text-sm px-4 py-2.5 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50 shadow"
            title="Create inventory records for products that don't have one yet"
          >
            <RefreshCw size={16} /> Backfill from Products
          </button>
          <button
            onClick={() => setOpenAdd(true)}
            className="flex items-center gap-2 text-white bg-emerald-600 hover:bg-emerald-700 font-medium rounded-lg text-sm px-5 py-2.5 shadow"
          >
            <Plus size={16} /> Add Item
          </button>
        </div>
      </div>

      <section className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">SKU / Barcode</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">On Hand</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Reserved</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Available</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Reorder</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Bin</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {(items || []).length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-400">
                    No inventory items yet. Use <span className="font-semibold">Backfill from Products</span> to seed from your catalog.
                  </td>
                </tr>
              ) : (
                pagedItems.map((i) => (
                  <tr key={i._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {i?.product?.product_name || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="font-mono">{i?.sku}</div>
                      <div className="text-xs text-gray-400 font-mono">{i?.barcode}</div>
                    </td>
                    <td className="px-4 py-4 text-center text-sm font-semibold">{i?.stock_on_hand}</td>
                    <td className="px-4 py-4 text-center text-sm text-gray-500">{i?.reserved}</td>
                    <td className="px-4 py-4 text-center"><Badge value={i?.available} threshold={i?.reorder_level} /></td>
                    <td className="px-4 py-4 text-center text-sm text-gray-500">{i?.reorder_level}</td>
                    <td className="px-4 py-4 text-sm text-gray-500">{i?.bin_location || "—"}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => { setSelected(i); setOpenAdjust(true); }} title="Adjust stock" className="text-blue-600 hover:text-blue-800 p-1.5"><Sliders size={16} /></button>
                        <button onClick={() => PrintLabel(i._id)} title="Print label" className="text-gray-600 hover:text-gray-900 p-1.5"><Printer size={16} /></button>
                        <button onClick={() => { setSelected(i); setOpenEdit(true); }} title="Edit" className="text-emerald-600 hover:text-emerald-800 p-1.5"><Edit3 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          pageSize={PAGE_SIZE}
          total={(items || []).length}
          onPageChange={setPage}
        />
      </section>

      {/* Add modal */}
      {openAdd && (
        <ProductModal closeModal={setOpenAdd} width="50%" title={"Add Inventory Item"}>
          <form
            onSubmit={(e) => { e.preventDefault(); CreateInventoryItem(addForm); }}
            className="p-4 grid gap-4 md:grid-cols-2"
          >
            <div className="md:col-span-2">
              <label className="block mb-2 text-sm font-bold text-gray-700">Product *</label>
              <select
                required
                value={addForm.product}
                onChange={(e) => setAddForm({ ...addForm, product: e.target.value })}
                className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
              >
                <option value="">Select a product…</option>
                {products.map((p) => (
                  <option key={p._id} value={p._id}>{p.product_name}</option>
                ))}
              </select>
            </div>
            <NumberField label="Opening Stock" value={addForm.stock_on_hand} onChange={(v) => setAddForm({ ...addForm, stock_on_hand: v })} />
            <NumberField label="Reorder Level" value={addForm.reorder_level} onChange={(v) => setAddForm({ ...addForm, reorder_level: v })} />
            <NumberField label="Reorder Qty" value={addForm.reorder_qty} onChange={(v) => setAddForm({ ...addForm, reorder_qty: v })} />
            <NumberField label="Cost Price" value={addForm.cost_price} onChange={(v) => setAddForm({ ...addForm, cost_price: v })} />
            <div className="md:col-span-2">
              <label className="block mb-2 text-sm font-bold text-gray-700">Bin Location</label>
              <input value={addForm.bin_location} onChange={(e) => setAddForm({ ...addForm, bin_location: e.target.value })} className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5" placeholder="e.g. A-12" />
            </div>
            <ModalActions onCancel={() => setOpenAdd(false)} loading={isLoading} label="Create Item" />
          </form>
        </ProductModal>
      )}

      {/* Edit modal */}
      {openEdit && (
        <ProductModal closeModal={setOpenEdit} width="50%" title={`Edit — ${selected?.product?.product_name || selected?.sku}`}>
          <form
            onSubmit={(e) => { e.preventDefault(); UpdateInventoryItem(selected?._id, editForm); }}
            className="p-4 grid gap-4 md:grid-cols-2"
          >
            <NumberField label="Reorder Level" value={editForm.reorder_level} onChange={(v) => setEditForm({ ...editForm, reorder_level: v })} />
            <NumberField label="Reorder Qty" value={editForm.reorder_qty} onChange={(v) => setEditForm({ ...editForm, reorder_qty: v })} />
            <NumberField label="Cost Price" value={editForm.cost_price} onChange={(v) => setEditForm({ ...editForm, cost_price: v })} />
            <div>
              <label className="block mb-2 text-sm font-bold text-gray-700">Status</label>
              <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5">
                <option value="active">Active</option>
                <option value="discontinued">Discontinued</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block mb-2 text-sm font-bold text-gray-700">Bin Location</label>
              <input value={editForm.bin_location} onChange={(e) => setEditForm({ ...editForm, bin_location: e.target.value })} className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5" />
            </div>
            <p className="md:col-span-2 text-xs text-gray-400">Stock quantity is changed via <span className="font-semibold">Adjust</span> or the Scan station, not here — every change is ledgered.</p>
            <ModalActions onCancel={() => setOpenEdit(false)} loading={isLoading} label="Save Changes" />
          </form>
        </ProductModal>
      )}

      {/* Adjust modal */}
      {openAdjust && (
        <ProductModal closeModal={setOpenAdjust} width="40%" title={`Adjust Stock — ${selected?.product?.product_name || selected?.sku}`}>
          <form
            onSubmit={(e) => { e.preventDefault(); AdjustStock(selected?._id, { qty_delta: Number(adjustForm.qty_delta), reason: adjustForm.reason }); }}
            className="p-4"
          >
            <p className="text-sm text-gray-500 mb-4">Current on hand: <span className="font-bold text-gray-800">{selected?.stock_on_hand}</span>. Use a positive number to add, negative to remove.</p>
            <NumberField label="Quantity change (+/-)" value={adjustForm.qty_delta} onChange={(v) => setAdjustForm({ ...adjustForm, qty_delta: v })} allowNegative />
            <div className="mt-4">
              <label className="block mb-2 text-sm font-bold text-gray-700">Reason *</label>
              <input required value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5" placeholder="e.g. Damaged units, recount" />
            </div>
            <ModalActions onCancel={() => setOpenAdjust(false)} loading={isLoading} label="Apply Adjustment" />
          </form>
        </ProductModal>
      )}

      <ToastContainer autoClose={3000} />
    </RenderInventoryPage>
  );
}

const NumberField = ({ label, value, onChange, allowNegative }) => (
  <div>
    <label className="block mb-2 text-sm font-bold text-gray-700">{label}</label>
    <input
      type="number"
      value={value}
      min={allowNegative ? undefined : 0}
      onChange={(e) => onChange(e.target.value)}
      className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
    />
  </div>
);

const ModalActions = ({ onCancel, loading, label }) => (
  <div className="md:col-span-2 flex items-center justify-end space-x-3 pt-4 border-t mt-2">
    <button type="button" onClick={onCancel} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
    <button type="submit" disabled={loading} className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 shadow">{loading ? "Processing…" : label}</button>
  </div>
);

export default InventoryItems;
